import Papa from 'papaparse';
import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';

import { getPlatformIconUrl } from 'src/utils/platform-icon';
import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

import { useJobProgress } from 'src/JobProgressContext';
import { startInkfrogImport } from 'src/redux/slices/listing';

import Iconify from 'src/components/iconify';
import { Upload } from 'src/components/upload';
import { useSnackbar } from 'src/components/snackbar';

import InkfrogImportSummary from './inkfrog-import-summary';

const INKFROG_SIGNATURE_HEADERS = [
  'ebay user', 'ebay market', 'listing format', 'item specifics',
  'ebay custom label', 'gallery plus', 'giving works charity id',
];

// Every problem with the file has the same way out, so each message ends with it.
const UPLOAD_THE_INKFROG_FILE = 'Upload the CSV file you exported from inkFrog.';
const COULD_NOT_READ_FILE = `Couldn't read this file. ${UPLOAD_THE_INKFROG_FILE}`;

export default function InkfrogImportDialog ({ open, onClose, hasConnectedEbay }) {
  const { enqueueSnackbar } = useSnackbar();
  const { startInkfrogImport: trackJob } = useJobProgress();

  const [step, setStep] = useState('upload'); // 'upload' | 'summary' | 'uploading'
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleDrop = useCallback((acceptedFiles) => {
    setError(null);
    const csvFile = acceptedFiles[0];

    if (!csvFile) return;

    if (!csvFile.name.toLowerCase().endsWith('.csv')) {
      setError(`This isn't a CSV file. ${UPLOAD_THE_INKFROG_FILE}`);
      return;
    }

    // Parse CSV client-side with PapaParse (handles multiline quoted fields correctly)
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const { data, meta } = results;

          if (!data || data.length === 0) {
            setError(`This file is empty. ${UPLOAD_THE_INKFROG_FILE}`);
            return;
          }

          // Normalize headers to lowercase for matching
          const headers = (meta.fields || []).map(h => h.toLowerCase().trim());

          // Check for Inkfrog signature
          const matched = INKFROG_SIGNATURE_HEADERS.filter(h => headers.includes(h));
          if (matched.length < 3) {
            setError(`This doesn't look like the listings file from inkFrog. ${UPLOAD_THE_INKFROG_FILE}`);
            return;
          }

          // Build a lowercase→original header map so we can access row fields
          const headerMap = {};
          (meta.fields || []).forEach(h => { headerMap[h.toLowerCase().trim()] = h; });

          let totalRows = 0;
          let auctionCount = 0;
          let fixedCount = 0;
          let zeroQtyCount = 0;
          let withImagesCount = 0;
          let emptyTitleCount = 0;
          let withInventoryCount = 0;
          const ebayUsers = new Set();
          const ebayMarkets = new Set();

          data.forEach(row => {
            totalRows += 1;

            const title = (row[headerMap.title] || '').trim();
            if (!title) emptyTitleCount += 1;

            const format = (row[headerMap['listing format']] || '').trim();
            if (format === 'Chinese') auctionCount += 1;
            else if (format === 'FixedPriceItem') fixedCount += 1;

            const qty = parseInt(row[headerMap.quantity] || '', 10);
            if (qty === 0) zeroQtyCount += 1;

            // Count rows with both a title and non-zero quantity
            if (title && qty > 0) withInventoryCount += 1;

            const images = (row[headerMap.images] || '').trim();
            if (images) withImagesCount += 1;

            const ebayUser = (row[headerMap['ebay user']] || '').trim();
            if (ebayUser) ebayUsers.add(ebayUser);

            const market = (row[headerMap['ebay market']] || '').trim();
            if (market) ebayMarkets.add(market);
          });

          setFile(csvFile);
          setParsedData({
            totalRows,
            auctionCount,
            fixedCount,
            zeroQtyCount,
            withImagesCount,
            emptyTitleCount,
            withInventoryCount,
            ebayUsers: Array.from(ebayUsers),
            ebayMarkets: Array.from(ebayMarkets),
            fileName: csvFile.name,
            fileSize: csvFile.size,
          });
          setStep('summary');
        } catch (parseErr) {
          setError(COULD_NOT_READ_FILE);
        }
      },
      error: () => {
        setError(COULD_NOT_READ_FILE);
      },
    });
  }, []);

  const handleClose = useCallback(() => {
    setStep('upload');
    setFile(null);
    setParsedData(null);
    setError(null);
    setUploading(false);
    onClose();
  }, [onClose]);

  const handleStartImport = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setStep('uploading');

    try {
      trackEvent(ANALYTICS_EVENTS.LISTING_INKFROG_IMPORT_STARTED, {
        count: parsedData?.listings?.length,
      });
      const result = await startInkfrogImport(file);
      if (result.jobId) {
        trackJob(result.jobId);
        // The import itself runs as a background job; this marks a successful handoff.
        trackEvent(ANALYTICS_EVENTS.LISTING_INKFROG_IMPORT_COMPLETED, {
          count: parsedData?.listings?.length,
        });
        enqueueSnackbar('Importing your inkFrog listings. You can keep working while it runs.', { variant: 'success' });
        handleClose();
      }
    } catch (err) {
      setError(err.message || "The import didn't start. Press Start Import to try again.");
      setStep('summary');
    } finally {
      setUploading(false);
    }
  }, [file, parsedData, trackJob, enqueueSnackbar, handleClose]);

  const handleBack = useCallback(() => {
    setStep('upload');
    setFile(null);
    setParsedData(null);
    setError(null);
  }, []);

  return (
    <Dialog open={open} onClose={uploading ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src={getPlatformIconUrl('ebay')} alt="" sx={{ width: 24, height: 24 }} />
        Import from inkFrog
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step !== 'uploading' && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Check your listings after importing
            </Typography>
            <Typography variant="body2">
              Some photos or details from inkFrog may be missing, so take a quick look once it&apos;s
              done.
            </Typography>
          </Alert>
        )}

        {step === 'upload' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Upload the CSV file you exported from inkFrog. Listings for sale on your eBay account
              come in linked to it, and the rest come in as drafts you can publish later. Photos are
              copied over too.
            </Typography>

            <Upload
              accept={{ 'text/csv': ['.csv'] }}
              onDrop={handleDrop}
              placeholder={
                <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
                  <Iconify icon="eva:cloud-upload-fill" width={40} sx={{ color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary">
                    Drop your inkFrog CSV file here or click to browse
                  </Typography>
                </Stack>
              }
            />
          </Stack>
        )}

        {step === 'summary' && parsedData && (
          <InkfrogImportSummary data={parsedData} hasConnectedEbay={hasConnectedEbay} />
        )}

        {step === 'uploading' && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <Iconify icon="svg-spinners:pulse-3" width={48} sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle1">Uploading your file…</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a moment for large files.
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'upload' && (
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
        )}

        {step === 'summary' && (
          <>
            <Button onClick={handleBack} color="inherit">
              Back
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleStartImport}
              loading={uploading}
              startIcon={<Iconify icon="eva:cloud-upload-fill" />}
            >
              Start Import ({parsedData?.totalRows} listing{parsedData?.totalRows === 1 ? '' : 's'})
            </LoadingButton>
          </>
        )}

        {step === 'uploading' && (
          <Button disabled color="inherit">
            Importing...
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

InkfrogImportDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  hasConnectedEbay: PropTypes.bool,
};
